import * as React from 'react';
import ReactSVG from 'react-inlinesvg';

import _ from '../../locale';
import { styled } from '../../theme';
import { ChartZoomConsumer, Context } from '../ChartZoomProvider';
import { Button, FieldLabel, FlexColumn, FlexRow } from '../index';
import resetZoomIcon from './svg/global-map-reset-zoom.svg';
import zoomInIcon from './svg/global-map-zoom-in.svg';
import zoomOutIcon from './svg/global-map-zoom-out.svg';

const IconWrapper = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',

    span: {
        height: 20,
    },
}));

const StyledButton = styled(Button)(({ theme }) => ({
    padding: '6px 20px',
    marginTop: 4,
    width: 40,
    minWidth: 40,
    height: 40,
    minHeight: 40,
    cursor: 'pointer',
    borderRadius: theme.borderRadius.base,
    display: 'inline-flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    boxSizing: 'border-box',
    background: theme.color.base1,
    border: '2px solid ' + theme.color.baseText,
    color: theme.color.baseText,
    boxShadow: 'none',
    svg: {
        color: theme.color.baseText,
    },
    ':hover': {
        background: theme.color.base2,
        color: theme.color.base1,
        svg: {
            color: theme.color.base1,
        },
        boxShadow: 'none',
    },
    ':active': {
        background: theme.color.base3,
        color: theme.color.base1,
        svg: {
            color: theme.color.base1,
        },
        boxShadow: 'none',
    },
}));

const Container = styled(FlexColumn)(({ theme }) => ({
    fontSize: '1.3rem',
    textTransform: 'uppercase',
    fontWeight: theme.typography.fontWeight.bold,
    color: theme.color.gray40,
    justifySelf: 'flex-end',

    'button + button': {
        marginLeft: 5,
    },

    '> button > img': {
        maxWidth: 'none',
    },
}));

const StyledFieldLabel = styled(FieldLabel)(({ theme }) => ({
    display: 'block',
    fontWeight: theme.typography.fontWeight.medium,
}));

type Props = Context & {
    className?: string;
};

const renderButton = (icon, clickHandler, description) => (
    <StyledButton onClick={clickHandler}>
        <IconWrapper>
            <ReactSVG src={icon} description={description} title={description} />
        </IconWrapper>
    </StyledButton>
);

const MapChartZoomControls: React.FC<Props> = ({ className, setZoom, getZoom, resetZoom }) => (
    <Container className={className}>
        <StyledFieldLabel>{_('map_chart_zoom_controls_label')}</StyledFieldLabel>
        <FlexRow align="center">
            {renderButton(zoomInIcon, () => setZoom(getZoom() * 1.5), _('map_chart_zoom_in'))}
            {renderButton(zoomOutIcon, () => setZoom(getZoom() * 0.5), _('map_chart_zoom_out'))}
            {renderButton(resetZoomIcon, resetZoom, _('map_chart_reset_zoom'))}
        </FlexRow>
    </Container>
);

export default (props) => (
    <ChartZoomConsumer>
        {(zoomProps) => <MapChartZoomControls {...zoomProps} {...props} />}
    </ChartZoomConsumer>
);
